import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { LayoutPreset, StageLayout } from '../../../shared/protocol.ts';
import type { RemotePeer } from '../lib/mesh.ts';
import { muteState } from '../lib/sources.ts';
import type { StudioEngine } from '../lib/studio.ts';
import { AudioEl, Avatar, Icon, MuteBadge, useObservable, VideoEl } from './common.tsx';

const PRESETS: { id: LayoutPreset; label: string; hint: string }[] = [
  { id: 'grid', label: 'Grid', hint: 'Everyone the same size' },
  { id: 'speaker', label: 'Speaker', hint: 'One person large, the rest in a strip' },
  { id: 'screen', label: 'Screen + strip', hint: 'A shared screen large, people beside it' },
  { id: 'side', label: 'Side by side', hint: 'Two large, side by side' },
];
const EMPTY: StageLayout = { preset: 'grid', order: [], focus: null, hidden: [] };

interface Tile {
  /** `${participantId}:camera` or `${participantId}:screen`: the same name in everyone's browser. */
  key: string;
  kind: 'camera' | 'screen';
  self: boolean;
  name: string;
  peer?: RemotePeer;
}

function useSize(ref: React.RefObject<HTMLElement | null>) {
  const [size, setSize] = useState({ w: 800, h: 450 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

/** The most columns-by-rows arrangement that makes n 16:9 tiles as large as possible in w × h. */
function bestGrid(n: number, w: number, h: number, gap = 12) {
  let best = { cols: 1, tileW: 0 };
  for (let cols = 1; cols <= Math.max(1, n); cols++) {
    const rows = Math.ceil(n / cols);
    const tileW = Math.min((w - gap * (cols - 1)) / cols, ((h - gap * (rows - 1)) / rows) * (16 / 9));
    if (tileW > best.tileW) best = { cols, tileW };
  }
  return best;
}

/**
 * The room's stage, arranged by the host: everyone in the room sees the same tiles in the same arrangement, fitted
 * to their own window (a wide window puts the strip beside the main tile, a tall one below it). Hosts and
 * producers can change the arrangement from here. Sound plays whatever the arrangement: taking a tile off the
 * stage never mutes anyone.
 */
export function Stage({ engine }: { engine: StudioEngine }) {
  useObservable(engine.mesh);
  const ref = useRef<HTMLDivElement>(null);
  const { w, h } = useSize(ref);
  const staff = engine.role === 'host' || engine.role === 'producer';
  const layout = engine.room?.layout ?? EMPTY;
  const me = engine.participantId;
  const sm = engine.sources;
  const peers = [...(engine.mesh?.peers.values() ?? [])];

  // Every tile there could be, in a natural order: screens first, then you, then everyone else as they joined.
  const natural: Tile[] = [];
  if (sm.active.some((s) => s.kind === 'screen' && s.use.live)) natural.push({ key: `${me}:screen`, kind: 'screen', self: true, name: 'You' });
  for (const p of peers) if (p.liveSources.some((s) => s.kind === 'screen')) natural.push({ key: `${p.info.participantId}:screen`, kind: 'screen', self: false, name: p.info.name, peer: p });
  natural.push({ key: `${me}:camera`, kind: 'camera', self: true, name: engine.name });
  for (const p of peers) natural.push({ key: `${p.info.participantId}:camera`, kind: 'camera', self: false, name: p.info.name, peer: p });
  const byKey = new Map(natural.map((t) => [t.key, t]));
  const ordered = [...layout.order.map((k) => byKey.get(k)).filter((t): t is Tile => !!t), ...natural.filter((t) => !layout.order.includes(t.key))];
  // Hidden tiles leave everyone's stage, except your own camera, which you always see.
  const visible = ordered.filter((t) => !layout.hidden.includes(t.key) || (t.self && t.kind === 'camera'));
  const focus = visible.find((t) => t.key === layout.focus) ?? visible.find((t) => t.kind === 'screen') ?? visible.find((t) => !t.self) ?? visible[0];

  const change = (next: Partial<StageLayout>) => engine.setLayout({ ...layout, order: ordered.map((t) => t.key), ...next });
  const move = (key: string, by: number) => {
    const keys = ordered.map((t) => t.key);
    const i = keys.indexOf(key);
    const j = Math.max(0, Math.min(keys.length - 1, i + by));
    keys.splice(j, 0, ...keys.splice(i, 1));
    change({ order: keys });
  };
  const controls = (t: Tile) =>
    staff ? (
      <div className="tile-controls" onClick={(e) => e.stopPropagation()}>
        {layout.preset !== 'grid' && focus?.key !== t.key && (
          <button title="Make this the main tile" onClick={() => change({ focus: t.key })}>
            <Icon name="layers" size={13} /> Main
          </button>
        )}
        <button title="Earlier" aria-label={`Move ${t.name}’s tile earlier`} onClick={() => move(t.key, -1)}>
          ‹
        </button>
        <button title="Later" aria-label={`Move ${t.name}’s tile later`} onClick={() => move(t.key, 1)}>
          ›
        </button>
        {!(t.self && t.kind === 'camera') && (
          <button title="Take off everyone’s stage (sound keeps playing)" aria-label={`Hide ${t.name}’s tile`} onClick={() => change({ hidden: [...layout.hidden, t.key] })}>
            <Icon name="eyeOff" size={13} />
          </button>
        )}
      </div>
    ) : null;
  const render = (t: Tile, extra = '') => (
    <TileView key={t.key} engine={engine} tile={t} className={extra}>
      {controls(t)}
    </TileView>
  );

  const wide = w / Math.max(1, h) >= 1.25;
  let body: ReactNode;
  if (layout.preset === 'grid' || visible.length <= 1 || !focus) {
    const g = bestGrid(visible.length, w, h);
    body = (
      <div className="stage-grid" style={{ gridTemplateColumns: `repeat(${g.cols}, ${Math.floor(g.tileW)}px)` }}>
        {visible.map((t) => render(t))}
      </div>
    );
  } else {
    const rest = visible.filter((t) => t !== focus);
    const second = layout.preset === 'side' ? rest[0] : undefined;
    const strip = second ? rest.slice(1) : rest;
    // Speaker view keeps its strip below on most windows; the screen layout puts people beside the screen when wide.
    const stripBeside = layout.preset === 'screen' ? wide : w / Math.max(1, h) >= 2.1;
    body = (
      <div className={`stage-focus ${stripBeside ? 'beside' : 'below'}`}>
        <div className={`stage-main ${second ? 'pair' : ''}`}>
          {render(focus, 'main')}
          {second && render(second, 'main')}
        </div>
        {strip.length > 0 && <div className="stage-strip">{strip.map((t) => render(t, 'small'))}</div>}
      </div>
    );
  }

  const hidden = ordered.filter((t) => layout.hidden.includes(t.key) && !(t.self && t.kind === 'camera'));
  return (
    <div className="stage-wrap">
      {staff && (
        <div className="stage-bar">
          <div className="segmented" role="radiogroup" aria-label="Stage layout">
            {PRESETS.map((p) => (
              <button key={p.id} role="radio" aria-checked={layout.preset === p.id} className={layout.preset === p.id ? 'on' : ''} title={p.hint} onClick={() => change({ preset: p.id })}>
                {p.label}
              </button>
            ))}
          </div>
          <span className="faint small hide-narrow">Everyone in {engine.room?.name ?? 'the room'} sees this layout</span>
          {hidden.map((t) => (
            <button key={t.key} className="sm ghost" title="Put back on the stage" onClick={() => change({ hidden: layout.hidden.filter((k) => k !== t.key) })}>
              <Icon name="eye" size={13} /> {t.name}
              {t.kind === 'screen' ? ' (screen)' : ''}
            </button>
          ))}
        </div>
      )}
      <div className="stage" ref={ref}>
        {body}
      </div>
      {/* Everyone's sound, whether or not their tile is on the stage. */}
      <div hidden>
        {peers.map((p) => (
          <span key={p.info.id}>
            <AudioEl track={p.tracks.mic} />
            {p.liveSources.some((s) => s.kind === 'screen') && <AudioEl track={p.tracks.screenAudio} />}
          </span>
        ))}
      </div>
    </div>
  );
}

function TileView({ engine, tile, className, children }: { engine: StudioEngine; tile: Tile; className: string; children: ReactNode }) {
  const sm = engine.sources;
  if (tile.kind === 'screen') {
    const local = tile.self ? sm.active.find((s) => s.kind === 'screen' && s.use.live) : undefined;
    const label = tile.self ? local?.label : tile.peer?.liveSources.find((s) => s.kind === 'screen')?.label;
    return (
      <div className={`tile screen ${className}`}>
        <VideoEl track={tile.self ? local?.track : tile.peer?.tracks.screen} />
        <div className="label">
          <Icon name="screen" size={14} /> {tile.self ? 'You' : tile.name} · {label}
        </div>
        {children}
      </div>
    );
  }
  if (tile.self) {
    const cam = sm.primaryCamera;
    const mic = sm.primaryMic;
    return (
      <div className={`tile self ${className} ${mic?.hardMuted || cam?.hardMuted ? 'hard-muted' : ''}`}>
        {cam && muteState(cam) === 'none' ? (
          <VideoEl track={cam.track} mirror />
        ) : (
          <div className="placeholder">
            <Avatar name={engine.name} size={76} />
          </div>
        )}
        <div className="label">
          <MicMark mute={mic ? muteState(mic) : 'hard'} />
          <span className="ellipsis">{engine.name} (you)</span>
        </div>
        <div className="badge">
          {engine.recorders.size > 0 && (
            <span className="pill rec" title="Recording on this computer">
              <span className="dot pulse" /> REC
            </span>
          )}
          {engine.recording && engine.room?.record === false && (
            <span className="pill" title="This room isn’t recorded">
              Not recorded here
            </span>
          )}
          {mic && <MuteBadge kind="microphone" mute={muteState(mic)} />}
          {cam && <MuteBadge kind="camera" mute={muteState(cam)} />}
        </div>
        {children}
      </div>
    );
  }
  const p = tile.peer!;
  const camSrc = p.liveSources.find((s) => s.kind === 'camera');
  const micSrc = p.liveSources.find((s) => s.kind === 'microphone');
  const st = engine.peers.get(p.info.id)?.status;
  return (
    <div className={`tile ${className} ${camSrc?.mute === 'hard' || micSrc?.mute === 'hard' ? 'hard-muted' : ''}`}>
      {camSrc?.mute === 'none' && p.tracks.camera ? (
        <VideoEl track={p.tracks.camera} />
      ) : (
        <div className="placeholder">
          <Avatar name={p.info.name} size={76} />
        </div>
      )}
      <div className="label">
        {micSrc ? <MicMark mute={micSrc.mute} /> : <span className="faint" title="You don’t receive their microphone">–</span>}
        <span className="ellipsis">{p.info.name}</span>
        {p.state !== 'connected' && <span className="faint">· {p.state}</span>}
      </div>
      <div className="badge">
        {st?.recording && (
          <span className="pill rec" title={`Recording on ${p.info.name}’s computer`}>
            <span className="dot" /> REC
          </span>
        )}
        {micSrc && <MuteBadge kind="microphone" mute={micSrc.mute} />}
        {camSrc && <MuteBadge kind="camera" mute={camSrc.mute} />}
        {p.tier !== 'high' && p.state === 'connected' && (
          <span className="pill" title="Live call quality reduced to protect bandwidth. Recordings are unaffected.">
            Live: {p.tier}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

/** The mic state at the start of a video tile's name label. */
export function MicMark({ mute }: { mute: 'none' | 'send' | 'hard' }) {
  return (
    <span className={mute === 'none' ? 'mic-ok' : mute === 'send' ? 'mic-send' : 'mic-hard'} style={{ display: 'inline-flex' }}>
      <Icon name={mute === 'none' ? 'mic' : 'micOff'} size={14} />
    </span>
  );
}
